/**
 * StageAssociationCard - Content for CRM stage association add/remove actions.
 *
 * A hook-free dispatcher over two renderings:
 *
 *   - A stage change on an EXISTING task (`ahoy_task`, association added)
 *     routes to TaskStageUpdateActionCard: the canonical TaskCard with the
 *     Status row showing the `current → proposed` diff in place. Tasks are
 *     NOT notes — they never render through the note-shaped preview.
 *   - Everything else renders below (GenericStageAssociationCard): the rich
 *     entity preview from ENTITY_CONTENT_REGISTRY plus a DiffSection row
 *     with the current → proposed stage labels — a stage change is an
 *     association update, so it renders as the same diff as every other
 *     association and property change rather than a bespoke stage surface.
 *
 * The two sides come from different reads of the same record, and the
 * distinction is the whole point:
 *   - current  → `useCrmObjectDiff().current`, the record with NO proposal
 *     projected onto it. Reading the projected record here (or a prior-target
 *     field copied onto the payload) is what makes both columns show the
 *     same stage.
 *   - proposed → this action's own `target_entity_id`; the action IS the
 *     proposal.
 *
 * In edit mode, the DiffSection proposed column renders AssociatedPipelineStageInput
 * (the same stage dropdown used in CRM slideouts).
 *
 * Shell is provided by the parent.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { useCrmObjectDiff } from '@/lib/crm/hooks';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';

import type {
  AssociationAddedEntityPayload,
  AssociationRemovedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isAssociationPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import { ActionEntityPreview } from './ActionEntityPreview';
import { ActionStageBadge } from './ActionStageBadge';
import { type DiffRow, DiffSection } from './DiffSection';
// The local `function findAssociationTarget` duplicate was removed from this
// file; the implementation now lives in `./shared/utils.ts` and is imported
// below.
import {
  ActionCardContent,
  ActionError,
  findAssociationTarget,
  useActionPayloadSave,
  useStageEditState,
} from './shared';
import { TaskStageUpdateActionCard } from './TaskStageUpdateActionCard';

export function StageAssociationCard(props: ActionRendererProps) {
  const { action, mode } = props;
  // Routing only — no hooks here, so the two renderings below each keep
  // their own unconditional hook order.
  if (!isAssociationPayload(action.payload)) {
    return (
      <ActionError message='Invalid stage association payload' mode={mode} />
    );
  }
  // A stage ADD on a task renders as the canonical TaskCard with an in-place
  // Status diff. Removals (rare; supportsEditing: false) and every other
  // entity type keep the generic entity-preview + DiffSection rendering.
  if (
    action.payload.entity_type_name === 'ahoy_task' &&
    action.payload.event_type_name === 'ahoy_association_added'
  ) {
    return (
      <TaskStageUpdateActionCard
        action={action as ProposedAction<AssociationAddedEntityPayload>}
        mode={mode}
        enrichedData={props.enrichedData}
        orgId={props.orgId}
        proposalId={props.proposalId}
        onUpdate={props.onUpdate}
      />
    );
  }
  return <GenericStageAssociationCard {...props} />;
}

function GenericStageAssociationCard({
  action,
  mode,
  enrichedData,
  orgId,
  proposalId,
  onUpdate,
}: ActionRendererProps) {
  const { isEditing, setDirty, saveHandlerRef, cancelHandlerRef } =
    useActionEdit();
  const isValid = isAssociationPayload(action.payload);

  // Cast upfront — guarded by isValid check below (early return before any usage)
  const typedPayload = action.payload as
    | AssociationAddedEntityPayload
    | AssociationRemovedEntityPayload;
  const entity_id = isValid ? typedPayload.entity_id : '';
  const entity_type_name = isValid ? typedPayload.entity_type_name : '';
  const event_type_name = isValid ? typedPayload.event_type_name : '';
  const association_type_name = isValid
    ? typedPayload.payload.association_type_name
    : '';
  const target_entity_id = isValid ? typedPayload.payload.target_entity_id : '';

  // The payload's entity_type_name is the source entity type (e.g. ahoy_contact).
  // No hardcoded mapping needed — works for custom entity types automatically.
  const entityType = entity_type_name;
  const isRemoval = event_type_name === 'ahoy_association_removed';
  const entityLabel =
    enrichedData?.object_label ||
    `${entity_type_name.replace('ahoy_', '')} record`;
  const crmEntityId = entity_id || undefined;

  // Look up the human-readable label for this association type from the schema
  // (e.g. "Lifecycle Stage", "Pipeline Stage") instead of formatting the raw name.
  const { associationTypes } = useAssociationTypes(orgId);
  const assocLabel = useMemo(() => {
    const assoc = associationTypes.find(
      a => a.association_type_name === association_type_name
    );
    return assoc?.label ?? association_type_name;
  }, [associationTypes, association_type_name]);

  // Both sides of the diff are the SAME record read two ways: the live store
  // record (what the stage is right now) and the projected record (what this
  // proposal makes it). Subscriptions are shared at the store level, so two
  // reads are still one fetch — and the rich entity preview below reuses them.
  //
  // Reading the projected record for the CURRENT side is what made both
  // columns show the proposed stage: inside a CrmPreviewProvider the proposal
  // is already merged into it. Reading a payload snapshot for that side is not
  // the fix either — it is a copy taken at proposal time that goes stale the
  // moment the record moves. Live on the left, projected on the right.
  const { current: liveObject } = useCrmObjectDiff(crmEntityId, orgId);

  const liveStageEntityId = useMemo(
    () => findAssociationTarget(liveObject, association_type_name),
    [liveObject, association_type_name]
  );

  // A removal proposes "no stage", so the stage named in the payload is the
  // one being taken away — it belongs on the left, with nothing on the right.
  const currentValue = isRemoval ? target_entity_id : liveStageEntityId;
  // The right-hand side is this action's own target: the action IS the
  // proposal, so projecting it onto the record is just "the target it names".
  // Deliberately NOT the projected store read — outside a CrmPreviewProvider
  // the projection equals the live record, which would render the current
  // stage as the proposed one.
  const proposedValue = isRemoval ? null : target_entity_id;

  // ── Edit state ──────────────────────────────────────────────────────────
  const { editStageId, handleStageChange, resetToProposed } = useStageEditState(
    {
      proposedValue: proposedValue ?? null,
      orgId,
      entityType,
    }
  );

  const isDirty = useMemo(
    () => editStageId !== (proposedValue ?? null),
    [editStageId, proposedValue]
  );

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const save = useCallback(async () => {
    const updatedPayload = {
      ...typedPayload,
      payload: {
        ...typedPayload.payload,
        target_entity_id: editStageId ?? '',
      },
    };
    await savePayload(updatedPayload);
  }, [editStageId, typedPayload, savePayload]);

  const cancel = useCallback(() => {
    resetToProposed();
  }, [resetToProposed]);

  useLayoutEffect(() => {
    saveHandlerRef.current = save;
    cancelHandlerRef.current = cancel;
  });

  // ── Diff rows ───────────────────────────────────────────────────────────

  const showEditInput = isEditing && !isRemoval;

  // A stage change is an association update, so it renders as the same
  // current → proposed diff every other association and property update uses.
  // Read-only display uses ActionStageBadge — the single stage renderer for
  // actions: pipeline name shown when the object type has multiple pipelines,
  // stage description on hover.
  const diffRows = useMemo<DiffRow[]>(() => {
    const currentDisplay = currentValue ? (
      <ActionStageBadge
        value={currentValue}
        orgId={orgId}
        entityType={entityType}
        size='sm'
        muted
      />
    ) : (
      <span className='text-sm text-[var(--color-text-tertiary)]'>
        (not set)
      </span>
    );

    const proposedDisplay = proposedValue ? (
      <ActionStageBadge
        value={proposedValue}
        orgId={orgId}
        entityType={entityType}
        size='sm'
      />
    ) : (
      <span className='text-sm text-[var(--color-text-tertiary)]'>
        (removed)
      </span>
    );

    return [
      {
        id: association_type_name,
        label: assocLabel,
        currentValue: currentDisplay,
        isEditable: showEditInput,
        proposedValue: showEditInput ? (
          <AssociatedPipelineStageInput
            value={editStageId}
            onChange={handleStageChange}
            entityType={entityType}
            associationTypeName={association_type_name}
            ariaLabel={`New value for ${assocLabel}`}
          />
        ) : (
          proposedDisplay
        ),
      },
    ];
  }, [
    currentValue,
    proposedValue,
    orgId,
    association_type_name,
    assocLabel,
    showEditInput,
    editStageId,
    handleStageChange,
    entityType,
  ]);

  // Early return AFTER all hooks
  if (!isValid) {
    return (
      <ActionError message='Invalid stage association payload' mode={mode} />
    );
  }

  // Strip target_object before passing enrichedData to ActionEntityPreview —
  // target_object here is the stage entity, not a related CRM record, and
  // preview builders would misread it as a company/contact association target.
  const entityEnrichedData = enrichedData
    ? { ...enrichedData, target_object: undefined }
    : undefined;

  return (
    <ActionCardContent>
      <ActionEntityPreview
        entityId={crmEntityId}
        entityType={entityType}
        label={entityLabel}
        enrichedData={entityEnrichedData}
        orgId={orgId}
      />
      {typedPayload.context_message && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={typedPayload.context_message}
          orgId={orgId}
          size='compact'
          resolvedSlots={action.enriched_data?.resolved_slots}
        />
      )}
      <DiffSection rows={diffRows} />
    </ActionCardContent>
  );
}
